import React from 'react';
import PropTypes from 'prop-types';
import { useMediaQuery } from 'react-responsive';

import {
  makeStripeConnectedAccountOnboardingLink,
} from 'common/stripeHelpers';

import {
  MEDIA_QUERY_SMALL_SCREEN,
  MEDIA_QUERY_MEDIUM_SCREEN_700,
} from 'client/constants/mediaQueries';

import useBranch from 'client/hooks/useBranch';

import SrBox from 'client/components/SrBox';
import SrFlex from 'client/components/SrFlex';
import SrText from 'client/components/SrText';
import SrButton, {
  SR_BUTTON_HEIGHT_TALL,
  SR_BUTTON_THEME_ADOBE_BLUE,
  SR_BUTTON_THEME_ADOBE_GREY,
  SR_BUTTON_THEME_ADOBE_GREEN,
} from 'client/components/SrButton/SrButton';
import SrModalV2, {
  MODAL_THEME_FIXED_FOOTER_BUTTON,
} from 'client/components/SrModalV2/SrModalV2';
import SrLink from 'client/components/SrLink/SrLink';
import SrExternalLink from 'client/components/SrExternalLink';
import SrLinkUrlButton from 'client/components/SrLinkUrlButton';

const SrPartnerBankAccountInviteModal = ({
  listing,
  partner,
  connectBankAccountFormUrl,
  onClose,
}) => {
  const { appUrl } = useBranch({
    appUrl: ['env', 'APP_URL'],
  });

  const isSmallScreen = useMediaQuery({
    query: MEDIA_QUERY_SMALL_SCREEN,
  });

  const isMediumScreen = useMediaQuery({
    query: MEDIA_QUERY_MEDIUM_SCREEN_700,
  });

  const connectBankAccountUrl = makeStripeConnectedAccountOnboardingLink({
    appUrl,
    listingId: listing?.id,
    partnerId: partner?.id,
  });

  const title = listing?.addressStreetDisplay || partner?.displayName;

  const stripeAddBankAccountTextMessage = global.encodeURIComponent(
    `${title}. Renters are paying their deposit & move-in fees through Sunroom. ` +
    'Connect your bank or debit card to cash out!\n\n' +
    connectBankAccountUrl
  );
  const stripeAddBankAccountEmailMessage = global.encodeURIComponent(
    `${title}. Renters are paying their deposit & move-in fees through Sunroom. ` +
    'Connect your bank or debit card to cash out!\n\n' +
    connectBankAccountUrl
  );
  const stripeAddBankAccountSubject = global.encodeURIComponent(
    `${title} – Collect deposit & move-in fees!`
  );

  return (
    <SrModalV2
      theme={MODAL_THEME_FIXED_FOOTER_BUTTON}
      buttonTheme={SR_BUTTON_THEME_ADOBE_GREY}
      onClose={onClose}
      contentMaxWidth='440px'
      contentPadding='60px 30px 0'
    >
      <SrBox>
        <SrBox margin='0 auto 19px' maxWidth='100px'>
          <img width='100%' height='auto' src='/images/share-link-blue.png' alt='share' />
        </SrBox>
        <SrText
          color='blue'
          font='greycliff'
          weight='800'
          fontSize='26px'
          lineHeight='33px'
          display='block'
          maxWidth={isSmallScreen ? '315px' : null}
          align='center'
        >
          {isMediumScreen
            ? 'Invite your client to collect the funds'
            : 'Share this link with your client so they can collect the funds'
          }
        </SrText>
      </SrBox>
      <SrBox marginTop='33px' idth='100%'>
        {isMediumScreen ?
          <SrFlex justify='center'>
            <SrExternalLink
              link={
                `mailto:?subject=${stripeAddBankAccountSubject}` +
              `&body=${stripeAddBankAccountEmailMessage}`
              }
              style={{ width: isMediumScreen ? '100%' : '250px' }}
            >
              <SrButton
                theme={SR_BUTTON_THEME_ADOBE_BLUE}
                heightType={SR_BUTTON_HEIGHT_TALL}
                hasBoxShadow
                type='button'
                minWidth={isMediumScreen ? '100%' : '250px'}
              >
                Email
              </SrButton>
            </SrExternalLink>
            <SrBox width={isSmallScreen ? '9px' : '20px'} />
            <SrExternalLink
              link={`sms:%20&body=${stripeAddBankAccountTextMessage}`}
              style={{ width: isMediumScreen ? '100%' : '250px' }}
            >
              <SrButton
                theme={SR_BUTTON_THEME_ADOBE_GREEN}
                heightType={SR_BUTTON_HEIGHT_TALL}
                hasBoxShadow
                type='button'
                minWidth={isMediumScreen ? '100%' : '250px'}
              >
                Text
              </SrButton>
            </SrExternalLink>
          </SrFlex> :
          <SrLinkUrlButton href={connectBankAccountUrl} />
        }

        {connectBankAccountFormUrl && (
          <>
            <SrBox margin='33px 30%'>
              <SrFlex align='center' justify='center'>
                <SrBox width='100%' height='1px' backgroundColor='rgba(123, 132, 136, 0.16)' />
                <SrBox margin='0 14px'>
                  <SrText
                    color='grey400'
                    font='greycliff'
                    fontSize='13px'
                    lineHeight='16px'
                    weight='600'
                    align='center'
                    letterSpacing='0.41px'
                  >
                    or
                  </SrText>
                </SrBox>
                <SrBox width='100%' height='1px' backgroundColor='rgba(123, 132, 136, 0.16)' />
              </SrFlex>
            </SrBox>
            <SrBox textAlign='center'>
              <SrLink to={connectBankAccountFormUrl}>
                <SrText color='blue300' weight='700' font='greycliff'>
                  I’m the agent, <br /> and I’m collecting the funds myself.
                </SrText>
              </SrLink>
            </SrBox>
          </>
        )}
      </SrBox>
    </SrModalV2>
  );
};

SrPartnerBankAccountInviteModal.propTypes = {
  listing: PropTypes.object,
  partner: PropTypes.object,
  connectBankAccountFormUrl: PropTypes.string,
  onClose: PropTypes.func.isRequired,
};

SrPartnerBankAccountInviteModal.displayName = 'SrPartnerBankAccountInviteModal';

export default SrPartnerBankAccountInviteModal;
